import { z } from "zod";
import { optionalEnum, optionalUuid } from "./_helpers/optional";
import { clampedLimit } from "./common.schema";

export const activityEventTypes = [
  "entity.created",
  "entity.updated",
  "entity.deleted",
  "import.started",
  "import.staged",
  "import.completed",
  "import.failed",
  "import.expired",
  "import.discarded",
  // ADV-921: standalone manual operator-note events (no underlying mutation).
  "operator.note",
] as const;

export const activityActorTypes = ["user", "api_key", "system"] as const;

export type ActivityActorType = (typeof activityActorTypes)[number];

export const activitySources = [
  "import",
  "ui",
  "api",
  "integration",
  "system",
] as const;

export type ActivitySource = (typeof activitySources)[number];

// ADV-919: structured origin envelope. Distinct from `source` — `source` is the
// legacy coarse vocabulary (ui/api/import/integration/system); `origin_type`
// classifies an event by the integration / channel / surface that produced it
// and is the column that activity-feed filters target. Sub-fields like the
// integration name and run-id stay in metadata.origin.* per the ticket.
export const activityOriginTypes = [
  "ui",
  "api",
  "csv_import",
  "integration_sync",
  "webhook",
  "system",
  "scheduled_job",
  "replay",
] as const;

export type ActivityOriginType = (typeof activityOriginTypes)[number];

/**
 * ADV-921 Phase 1: file attached to an activity event via a metadata convention.
 * Stored under `metadata.attachments[]`; promoted to a join table in Phase 2.
 */
export const ActivityEventAttachmentSchema = z.object({
  uploaded_file_id: z.string().uuid().describe("Uploaded File"),
  label: z.string().max(200).optional().describe("Label"),
});

export type ActivityEventAttachment = z.infer<
  typeof ActivityEventAttachmentSchema
>;

/**
 * ADV-921 Phase 1: optional audit-augmentation block carried alongside the
 * standard trigger-shape metadata (`changed_fields`, `before`, `after`).
 *
 * - `note`            — free-text operator rationale ("Taking over from Alex (OOO)").
 * - `attachments`     — pointers to `core.uploaded_files` (e.g. signed contract PDF).
 * - `correlation_id`  — groups events from a single HTTP request; auto-populated
 *                       from `TenantContext.correlationId` when not supplied.
 * - `caused_by_event_id` — explicit parent linkage for cascading mutations.
 *
 * All fields optional; future Phase 2 promotes to dedicated columns + join table.
 */
export const ActivityEventAuditSchema = z.object({
  note: z.string().min(1).max(4000).optional().describe("Note"),
  attachments: z
    .array(ActivityEventAttachmentSchema)
    .max(10)
    .optional()
    .describe("Attachments"),
  correlation_id: optionalUuid().describe("Correlation"),
  caused_by_event_id: optionalUuid().describe("Caused By Event"),
});

export type ActivityEventAudit = z.infer<typeof ActivityEventAuditSchema>;

export const ActivityEventSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().nullable().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  event_type: z.enum(activityEventTypes).describe("Event Type"),
  entity_type: z.string().nullable().optional().describe("Entity Type"),
  entity_id: z.string().uuid().nullable().optional().describe("Entity"),
  actor_type: z.enum(activityActorTypes).describe("Actor Type"),
  actor_id: z.string().nullable().optional().describe("Actor"),
  source: z.enum(activitySources).describe("Source"),
  origin_type: z
    .enum(activityOriginTypes)
    .nullable()
    .optional()
    .describe("Origin Type"),
  origin_id: z.string().uuid().nullable().optional().describe("Origin"),
  metadata: z.record(z.unknown()).nullable().optional().describe("Metadata"),
  created_at: z.string().describe("Created At"),
});

export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

export const ActivityEventInsertSchema = z.object({
  event_type: z.enum(activityEventTypes).describe("Event Type"),
  entity_type: z.string().optional().describe("Entity Type"),
  entity_id: optionalUuid().describe("Entity"),
  actor_type: z
    .enum(activityActorTypes)
    .default("system")
    .describe("Actor Type"),
  actor_id: z.string().optional().describe("Actor"),
  source: z.enum(activitySources).describe("Source"),
  origin_type: optionalEnum(activityOriginTypes).describe("Origin Type"),
  origin_id: optionalUuid().describe("Origin"),
  metadata: z.record(z.unknown()).optional().describe("Metadata"),
});

export type ActivityEventInsert = z.input<typeof ActivityEventInsertSchema>;

export const ActivityEventQuerySchema = z.object({
  entity_type: z.string().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  entity_id: z.string().uuid().optional(),
  event_type: z.string().optional(),
  source: z.string().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  import_job_id: z.string().uuid().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  // ADV-921: substring search over metadata->>'note' (pg_trgm GIN index).
  note_search: z.string().min(1).max(200).optional(),
  limit: clampedLimit(500, 50).optional(),
  offset: z.coerce.number().int().min(0).default(0).optional(),
});

export type ActivityEventQuery = z.infer<typeof ActivityEventQuerySchema>;

// ADV-1116: feed projection over core.activity_events. The SQL view
// `core.activity_events_feed_v` filters down to user-meaningful events
// (operator notes, stage/status/assignment changes, import lifecycle) and
// re-shapes them with a stable (kind, label, body) projection that the
// EntityActivityFeed UI (ADV-1115) reads against.
//
// `other` is the fall-through for an `entity.updated` whose `changed_fields`
// matches the allowlist but didn't hit one of the named branches (e.g. a
// crew_id-only or territory_id-only change). UI renders these with a
// generic "<entity> updated" label.
export const activityEventFeedKinds = [
  "stage_change",
  "status_change",
  "assignment",
  "operator_note",
  "import_lifecycle",
  "other",
] as const;

export type ActivityEventFeedKind = (typeof activityEventFeedKinds)[number];

export const ActivityEventFeedRowSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().nullable().describe("Business"),
  entity_type: z.string().nullable().describe("Entity Type"),
  entity_id: z.string().uuid().nullable().describe("Entity"),
  kind: z.enum(activityEventFeedKinds).describe("Kind"),
  label: z.string().describe("Label"),
  body: z.string().nullable().describe("Body"),
  actor_type: z.string().describe("Actor Type"),
  actor_id: z.string().nullable().describe("Actor"),
  // ADV-1344: resolved actor identity, appended to core.activity_events_feed_v
  // via the OR-bridge LATERAL (auth.users.id app-path OR contacts.id
  // import-path → team_member_attributes.headshot_url), business-scoped. NULL
  // for system/api_key actors and unresolved import actor_ids.
  actor_name: z.string().nullable().describe("Actor Name"),
  actor_avatar_url: z.string().nullable().describe("Actor Avatar"),
  source: z.string().describe("Source"),
  origin_type: z.string().nullable().describe("Origin Type"),
  origin_id: z.string().uuid().nullable().describe("Origin"),
  occurred_at: z.string().describe("Occurred At"),
  metadata: z.record(z.unknown()).describe("Metadata"),
});

export type ActivityEventFeedRow = z.infer<typeof ActivityEventFeedRowSchema>;

export const ActivityEventFeedQuerySchema = z
  .object({
    entity_type: z.string().min(1).optional(),
    // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
    entity_id: z.string().uuid().optional(),
    limit: clampedLimit(200).optional(),
    before: z.string().datetime().optional(),
  })
  .refine(
    (q) =>
      (q.entity_type === undefined && q.entity_id === undefined) ||
      (q.entity_type !== undefined && q.entity_id !== undefined),
    {
      message: "entity_type and entity_id must be provided together",
      path: ["entity_id"],
    },
  );

export type ActivityEventFeedQuery = z.infer<
  typeof ActivityEventFeedQuerySchema
>;
